import { TextFieldWithLoading } from 'components/TextFieldWithLoading'
import { BomItemController } from 'controllers/Project/BomItemController'
import { useAppController } from 'customHooks/useAppController'
import { BomItemPointer } from 'model/Project/BomItemPointer'
import { BomItemType } from 'model/Project/BoMItemRow'
import {
  Ref,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { ShowException } from 'store/Application/appActions'
import { useAppSelector } from 'store/configureStore'
import { bomItemActivityByIdSelector } from '../store/selectors/activities/bomItemActivityByIdSelector'

export type BomItemActivityCommentRef = {
  save: () => Promise<void>
  prepareCopy: () => () => Promise<void>
}

type Props = {
  ref?: Ref<BomItemActivityCommentRef>
  bomItemPointer: BomItemPointer
  bomItemActivityId: string
}

export const BomItemActivityComment = (props: Props) => {
  const { controller, loading } = useAppController(
    () => new BomItemController(),
  )
  const { t } = useTranslation()

  const activityComment = useAppSelector((state) => {
    const activity = bomItemActivityByIdSelector(
      props.bomItemPointer,
      props.bomItemActivityId,
    )(state)

    return activity?.comment
  })

  const [value, setValue] = useState(activityComment ?? '')
  const valueRef = useRef(activityComment ?? '')
  const observedComment = useRef(activityComment ?? '')
  const editedLocally = useRef(false)

  useEffect(() => {
    // An earlier autosave response must not overwrite a newer local comment.
    if (valueRef.current === observedComment.current) {
      valueRef.current = activityComment ?? ''
      setValue(valueRef.current)
    }
    observedComment.current = activityComment ?? ''
    savedComment.current = activityComment ?? ''
  }, [activityComment])

  const autoSaveTimeout = useRef<NodeJS.Timeout>(undefined)
  const pendingSave = useRef<Promise<void>>(Promise.resolve())
  const savedComment = useRef(activityComment ?? '')

  const [validation, setValidation] = useState<string | undefined>(undefined)
  const validate = useCallback(
    (comment: string) => {
      const remainingChars = 300 - comment.length
      if (remainingChars <= 40) {
        setValidation(t('common:chars-left', { count: remainingChars }))
      } else {
        setValidation(undefined)
      }
    },
    [setValidation, t],
  )
  const handleSaveComment = useCallback(
    async (comment: string, force = false) => {
      if (autoSaveTimeout.current) {
        clearTimeout(autoSaveTimeout.current)
      }

      const persist = async () => {
        if (force || comment !== savedComment.current) {
          await controller.updateActivityComment(props.bomItemPointer, {
            boMItemActivityId: props.bomItemActivityId,
            comment,
          })
          savedComment.current = comment
        }
      }
      // Serialize blur, debounce and explicit copy saves; retry after a prior failure.
      pendingSave.current = pendingSave.current.then(persist, persist)
      await pendingSave.current
    },
    [
      activityComment,
      controller,
      props.bomItemActivityId,
      props.bomItemPointer,
    ],
  )

  useImperativeHandle(
    props.ref,
    () => ({
      save: () => handleSaveComment(value ?? ''),
      prepareCopy: () => {
        const comment = valueRef.current
        const edited = editedLocally.current
        // Capture before the modal source save can refresh this field.
        return () =>
          edited ? handleSaveComment(comment, true) : pendingSave.current
      },
    }),
    [handleSaveComment, value],
  )

  useEffect(() => () => clearTimeout(autoSaveTimeout.current), [])

  const handleChange = useCallback(
    (e: { target: { value: string } }) => {
      if (autoSaveTimeout.current) {
        clearTimeout(autoSaveTimeout.current)
      }

      setValue(e.target.value)
      valueRef.current = e.target.value
      editedLocally.current = true
      validate(e.target.value)

      autoSaveTimeout.current = setTimeout(async () => {
        await handleSaveComment(e.target.value).catch((error) =>
          ShowException('comment', error),
        )
      }, 1500)
    },
    [handleSaveComment, validate],
  )

  return (
    <TextFieldWithLoading
      loading={loading[`update-activity-comment-${props.bomItemActivityId}`]}
      saveOnEnterFunction={(comment) =>
        handleSaveComment(comment).catch((error) =>
          ShowException('comment', error),
        )
      }
      name="comment"
      placeholder={t('common:comment')}
      variant="standard"
      onDoubleClick={(e) => e.stopPropagation()}
      multiline
      minRows={1}
      maxRows={4}
      fullWidth
      disabled={props.bomItemPointer.type === BomItemType.partInstance}
      value={value}
      onChange={handleChange}
      onBlur={(e) => {
        void handleSaveComment(e.currentTarget.value).catch((error) =>
          ShowException('comment', error),
        )
      }}
      inputProps={{
        style: {
          fontFamily: 'monospace',
          display: 'flex',
          alignItems: 'center',
        },
        maxLength: 300,
      }}
      helperText={validation}
    />
  )
}
